/**
 * useWarehouseProductPurchases — drives the Warehouse > Product
 * Purchases screen. Two pieces remain (2026-06-04):
 *
 *   - Per-product summary table (top): in_stock_units +
 *     pending_invoice_count + link_active per active product.
 *   - Receive modal: lists eligible Manage Purchases for a picked
 *     product, allocates a slice across variants, atomic batch
 *     POST /warehouse/product-purchases/allocate.
 *
 * The per-buy ledger was dropped; there is no `items / total / page`
 * pagination here anymore.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

/** Per-variant on-hand for the Receive modal. */
export interface VariantOnHandRef {
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly on_hand: number
  readonly is_low_stock: boolean
}

export interface ReceiveLine {
  readonly variant_id: string
  readonly quantity: number
}

/** One row in the per-product summary table at the top of the screen. */
export interface ProductAggregateDto {
  readonly product_id: string
  readonly product_name: string
  /** Live on-hand count for this product (Σ stock_movements across all
   *  active variants, signed by direction). Drives the In stock column. */
  readonly in_stock_units: number
  /** Count of bare Manage Purchases with remaining capacity for this
   *  product's linked suppliers. Drives the Pending invoices column. */
  readonly pending_invoice_count: number
  readonly link_active: boolean
}

/** Unused invoice for the modal's invoice dropdown (grouped by supplier). */
export interface UnusedInvoiceDto {
  readonly purchase_id: string       // = the manage purchase id (for /allocate)
  readonly invoice_no: string
  /** Manage Purchase REMAINING capacity in millis (decrements with each
   *  allocation; the MP drops out when it hits zero). */
  readonly purchased_millis: number
  readonly received_millis: number   // always 0 here; kept for shape compat
}

export interface SupplierWithInvoicesDto {
  readonly supplier_id: string
  readonly supplier_name: string
  readonly invoices: ReadonlyArray<UnusedInvoiceDto>
}

export interface ProductReceiveOptionsDto {
  readonly product: { readonly id: string; readonly name: string }
  readonly suppliers: ReadonlyArray<SupplierWithInvoicesDto>
  readonly variants: ReadonlyArray<VariantOnHandRef>
}

export interface UseWarehouseProductPurchasesReturn {
  /** Per-product summary (one row per active product). */
  readonly productAggregates: ProductAggregateDto[]
  readonly isAggregatesLoading: boolean
  readonly reloadAggregates: () => void
  /** Modal opens off a product row — fetches supplier/invoice/variant pickers. */
  readonly getReceiveOptionsByProduct: (
    productId: string
  ) => Promise<{ ok: true; data: ProductReceiveOptionsDto } | { ok: false; message: string }>
  /**
   * Snapshot a bare Manage Purchase onto a product as a new wpp row +
   * stock movements. Modal collects `{managePurchaseId, productId, lines}`.
   */
  readonly allocate: (
    input: { managePurchaseId: string; productId: string; lines: ReadonlyArray<ReceiveLine> }
  ) => Promise<{ ok: true; id: string } | { ok: false; field: string | null; message: string }>
}

export function useWarehouseProductPurchases(): UseWarehouseProductPurchasesReturn {
  const { authedFetch } = useAuth()

  const [productAggregates, setProductAggregates] = useState<ProductAggregateDto[]>([])
  const [isAggregatesLoading, setIsAggregatesLoading] = useState<boolean>(true)
  const [aggregatesTick, setAggregatesTick] = useState<number>(0)
  const reloadAggregates = useCallback((): void => setAggregatesTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setIsAggregatesLoading(true)
    ;(async () => {
      try {
        const res = await authedFetch(APIROUTES.WAREHOUSEPRODUCTPURCHASESBYPRODUCT, { signal: ac.signal })
        if (!res.ok) return
        const body = (await res.json()) as { success: true; data: { items: ProductAggregateDto[] } }
        if (!ac.signal.aborted) setProductAggregates(body.data.items)
      } catch {
        /* aborted */
      } finally {
        if (!ac.signal.aborted) setIsAggregatesLoading(false)
      }
    })()
    return () => ac.abort()
  }, [authedFetch, aggregatesTick])

  const getReceiveOptionsByProduct = useCallback(
    async (productId: string): Promise<
      | { ok: true; data: ProductReceiveOptionsDto }
      | { ok: false; message: string }
    > => {
      const url = fillPath(APIROUTES.WAREHOUSEPRODUCTRECEIVEOPTIONS, { productId })
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as { success: true; data: ProductReceiveOptionsDto }
      return { ok: true, data: body.data }
    },
    [authedFetch]
  )

  const allocate = useCallback(
    async (input: { managePurchaseId: string; productId: string; lines: ReadonlyArray<ReceiveLine> }) => {
      const r = await mutateJson<{ id: string }>(
        authedFetch,
        APIROUTES.WAREHOUSEPRODUCTPURCHASEALLOCATE,
        'POST',
        {
          manage_purchase_id: input.managePurchaseId,
          product_id: input.productId,
          lines: input.lines
        }
      )
      if (!r.ok) return r
      setAggregatesTick((t) => t + 1)
      return { ok: true as const, id: r.data.id }
    },
    [authedFetch]
  )

  return {
    productAggregates,
    isAggregatesLoading,
    reloadAggregates,
    getReceiveOptionsByProduct,
    allocate
  }
}
